import React, { useContext, useMemo, useState } from "react";
import { Empty, Radio, Select, Spin, Tooltip } from "antd";
import { LuCrown, LuUser } from "react-icons/lu";
import useFetchUsers from "../../hooks/useFetchUsers";
import useTeams from "../../hooks/useTeams";
import { UserContext } from "../../amplify/authenticator-provider";

const TEAM_ROLE = { LEADER: "leader", PARTICIPANT: "participant", NONE: "none" };

const roleForCurrentUser = (team, currentUserId) => {
  if (!currentUserId || !team) return TEAM_ROLE.NONE;
  if (+team.leaderId === +currentUserId) return TEAM_ROLE.LEADER;
  const isMember = (team.members || []).some(m => +m?.memberId === +currentUserId);
  return isMember ? TEAM_ROLE.PARTICIPANT : TEAM_ROLE.NONE;
};

// Sort priority: leader teams first, then participant teams, then everything
// else. Within each bucket, preserve the order the BE returned (which keeps
// any backend-side ordering intact).
const sortTeamsByCurrentUserRole = (teams, currentUserId) => {
  const bucketOrder = { [TEAM_ROLE.LEADER]: 0, [TEAM_ROLE.PARTICIPANT]: 1, [TEAM_ROLE.NONE]: 2 };
  const decorated = (teams || []).map((t, i) => ({
    team: t,
    role: roleForCurrentUser(t, currentUserId),
    originalIndex: i,
  }));
  decorated.sort((a, b) => {
    if (bucketOrder[a.role] !== bucketOrder[b.role])
      return bucketOrder[a.role] - bucketOrder[b.role];
    return a.originalIndex - b.originalIndex;
  });
  return decorated;
};

export const ENTITY_MODE = {
  USERS: "users",
  TEAMS: "teams",
};

const ALL = "all";

const userDisplayName = u => {
  if (!u) return "";
  if (u.fullName) return u.fullName;
  return `${u.firstName || ""} ${u.lastName || ""}`.trim();
};

/**
 * "Show data for" filter — single multi-select with a Users/Teams toggle
 * injected at the top of its dropdown panel. Selection state is tracked
 * separately per mode so switching modes doesn't clobber the other list's
 * picks, and only the active mode's IDs flow downstream (the parent decides
 * what to send to the API).
 *
 * Self-contained: users and teams are fetched internally (Apollo dedupes
 * with any other concurrent useTeams subscriber, e.g. DashboardFilterContext).
 * Callers that already have the lists in hand can still pass them via the
 * optional `teams` / `teamsLoading` props to avoid a redundant hook
 * subscription.
 *
 * Empty array on either side means "all" (matches the existing UserSelector
 * `allEmptyArray` convention).
 *
 * @param {object}   props
 * @param {"users"|"teams"} props.mode             Active mode.
 * @param {(mode:string)=>void} props.onModeChange Mode switcher.
 * @param {number[]} props.userIds                 Selected user IDs.
 * @param {number[]} props.teamIds                 Selected team IDs.
 * @param {(ids:number[])=>void} props.onUserIdsChange
 * @param {(ids:number[])=>void} props.onTeamIdsChange
 * @param {boolean}  [props.committed]             See inline note.
 * @param {Array}    [props.teams]                 Optional override; if absent we fetch.
 * @param {boolean}  [props.teamsLoading]          Optional override for the loading flag.
 * @param {string}   [props.className]
 */
const EntityFilter = ({
  mode = ENTITY_MODE.USERS,
  onModeChange,
  userIds = [],
  teamIds = [],
  onUserIdsChange,
  onTeamIdsChange,
  // True when the visible toggle (`mode`) matches the mode the user has
  // actually committed to. Drives the "placeholder vs All chip" decision —
  // when previewing a mode we haven't committed to, show placeholder; when
  // we ARE in the committed mode with `value === []`, show "All" chip
  // because the user explicitly picked the All option.
  committed = false,
  // The mode that is actually driving the API payload right now. When
  // provided, EntityFilter reverts the visible toggle back to this on
  // dropdown close if the user toggled but never selected — preventing a
  // stale "Select teams" placeholder while the API still filters by users.
  // Omit on standalone pickers (e.g. the media report) where every state
  // change is the working state and there is no separate "committed" mode.
  activeMode,
  // Optional escape hatches: parents that already loaded the team list (e.g.
  // DashboardFilterContext, which also needs it for the apiTeamIds expansion)
  // can hand it in. When omitted, the component fetches teams itself.
  teams: teamsProp,
  teamsLoading: teamsLoadingProp,
  // Same escape hatch for users — callers that already have the list (e.g.
  // a page running its own UserSelector / useFetchUsers elsewhere) can pass
  // it in. Omitted → component fetches with the same options it uses
  // everywhere (current-location + all-location users).
  users: usersProp,
  usersLoading: usersLoadingProp,
  // When true, the Select renders as read-only — used by callers that
  // need to pin a default selection the user cannot change (e.g. dashboard
  // ACTIVITY_CENTER_RESTRICT_REMOVE_DEFAULT_FILTER permission).
  disabled = false,
  // Single vs multi select. Defaults to multi (dashboard). When false the
  // control picks exactly one user/team; the value is still emitted as an array
  // (empty = "all") so the parent contract is unchanged.
  multiple = true,
  // Scope the internally-fetched users/teams to a specific location. Omit to use
  // the session location (`currentLocationId`), which is the dashboard default.
  // Callers with their own location filter (e.g. the Daily Checkout Report) pass
  // it so both lists refetch when that filter changes.
  locationId,
  className = "w-full",
  // When false, the Users/Teams toggle is hidden and the filter is locked to
  // users (multi-select). Used where team selection isn't supported yet.
  allowTeams = true,
  // When false, the leading "All users" / "All teams" select-all option is
  // omitted, so each entity must be picked explicitly (multi-select still).
  allowAll = true,
}) => {
  const isUsersMode = allowTeams ? mode === ENTITY_MODE.USERS : true;
  const { user } = useContext(UserContext);
  const currentUserId = user?.id ? +user.id : null;

  // Internal user source — only fires when the parent doesn't supply one.
  // `skip` is true when the prop is provided, so this hook is a true no-op
  // in that case (no subscription, no network).
  const [internalUserList, setInternalUserList] = useState([]);
  const { loading: internalUsersLoading } = useFetchUsers(
    null,
    res => setInternalUserList(res?.getUsers?.data || []),
    usersProp !== undefined,
    {
      isCurrentLocationUser: true,
      isAllLocationUsers: true,
      // Overrides the session location ids when the caller scopes by location.
      ...(locationId != null ? { locationIds: [Number(locationId)] } : {}),
    }
  );
  const userList = usersProp !== undefined ? usersProp : internalUserList;
  const usersLoading = usersLoadingProp !== undefined ? usersLoadingProp : internalUsersLoading;

  // Internal team source — only fires when the parent doesn't supply one, and
  // lazily: not until the user is actually in Teams mode or already has teams
  // selected (whose labels we then need). Fetching eagerly on mount whenever
  // `allowTeams` is true made opening the drawer (e.g. review edit, which is in
  // Users mode) pay for a teams query it never uses.
  const needsTeams = !isUsersMode || teamIds.length > 0;
  const { activeTeams, loading: internalTeamsLoading } = useTeams({
    skip: teamsProp !== undefined || !allowTeams || !needsTeams,
    locationId,
  });
  const teams = teamsProp !== undefined ? teamsProp : activeTeams;
  const teamsLoading = teamsLoadingProp !== undefined ? teamsLoadingProp : internalTeamsLoading;

  const value = isUsersMode ? userIds : teamIds;
  const loading = isUsersMode ? usersLoading : teamsLoading;
  const placeholder = isUsersMode ? "Select users" : "Select teams";

  const options = useMemo(() => {
    if (isUsersMode) {
      // Alphabetize users A→Z (case-insensitive). Done on a copy so we don't
      // mutate the upstream `userList` state held by `useFetchUsers`.
      const sortedUsers = [...userList].sort((a, b) =>
        userDisplayName(a).localeCompare(userDisplayName(b), undefined, {
          sensitivity: "base",
        })
      );
      return [
        ...(multiple && allowAll ? [{ value: ALL, label: "All users", name: "All users" }] : []),
        // "All" is only offered in multi-select; single-select picks one user.
        ...sortedUsers.map(u => {
          const name = userDisplayName(u);
          return { value: +u.id, label: name, name };
        }),
      ];
    }
    // No teams configured — surface zero options so AntD renders the
    // notFoundContent empty state below the toggle (no stray "All teams").
    if (teams.length === 0) return [];
    // Leader → Participant → Other ordering so the current user's teams
    // surface at the top of the list. The crown / user icon next to the
    // name encodes the role; teams the user isn't part of stay un-iconed.
    const sorted = sortTeamsByCurrentUserRole(teams, currentUserId);
    return [
      ...(multiple && allowAll ? [{ value: ALL, label: "All teams", name: "All teams" }] : []),
      ...sorted.map(({ team, role }) => ({
        value: +team.id,
        name: team.name,
        label: (
          <span className="inline-flex items-center gap-1.5">
            <span className="truncate">{team.name}</span>
            {role === TEAM_ROLE.LEADER ? (
              <Tooltip title="You lead this team" mouseEnterDelay={0.2}>
                <span className="inline-flex shrink-0">
                  <LuCrown size={14} className="text-amber-500" aria-label="Team leader" />
                </span>
              </Tooltip>
            ) : role === TEAM_ROLE.PARTICIPANT ? (
              <Tooltip title="You're a member of this team" mouseEnterDelay={0.2}>
                <span className="inline-flex shrink-0">
                  <LuUser size={14} className="text-blue-500" aria-label="Team member" />
                </span>
              </Tooltip>
            ) : null}
          </span>
        ),
      })),
    ];
  }, [isUsersMode, userList, teams, currentUserId, multiple, allowAll]);

  // Display logic:
  //   committed + value=[]   → "All <entity>" chip (user explicitly picked All)
  //   !committed + value=[]  → placeholder (this mode hasn't been chosen yet)
  //   value=[ids...]         → those entity chips
  const displayValue = useMemo(() => {
    // The "All" chip only exists when allowAll is on; otherwise an empty value
    // stays empty (placeholder), so clearing the field shows nothing.
    if (!value || value.length === 0) return committed && allowAll ? [ALL] : [];
    return value.map(v => +v);
  }, [value, committed, allowAll]);

  const handleChange = raw => {
    const setter = isUsersMode ? onUserIdsChange : onTeamIdsChange;
    if (!setter) return;
    // Single-select mode emits a scalar (or undefined on clear); normalize to
    // the array shape the logic below (and the parent contract) expects.
    const next = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
    // Cross-mode reset + activating the committed mode is handled by the
    // parent's `setUserIds` / `setTeamIds` setters now — this component just
    // emits the new id list for the current mode.

    // Was the dropdown displaying the "All" chip just before this change?
    // True when we're committed to this mode and the value list is empty.
    const wasShowingAll = committed && (!value || value.length === 0);
    const stillHasAll = next.includes(ALL);
    const realIds = next.filter(v => v !== ALL).map(v => +v);

    // Transition: "All" chip was on, user picked a specific entity → drop
    // ALL and keep the real picks. Without this, AntD emits ["all", newId]
    // and the next branch would wipe the new selection out.
    if (wasShowingAll && realIds.length > 0) {
      setter(realIds);
      return;
    }
    // Explicit ALL click → empty list (parent treats [] as "all").
    if (stillHasAll) {
      setter([]);
      return;
    }
    setter(realIds);
  };

  let notFoundContent;
  if (loading) {
    // Switching the Users/Teams toggle while the list is still loading — tell
    // the user what's being fetched instead of showing an empty dropdown.
    notFoundContent = (
      <div className="flex items-center justify-center gap-2 py-2 text-black-45 text-sm">
        <Spin size="small" />
        {isUsersMode ? "Fetching users…" : "Fetching teams…"}
      </div>
    );
  } else if (!isUsersMode && teams.length === 0) {
    notFoundContent = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No teams" />;
  }

  const dropdownRender = menu => (
    <>
      {/* preventDefault on mousedown keeps this custom header from stealing focus
          from the Select's search box and from being interpreted as an option
          click — otherwise toggling mode mid-search flips the selection. */}
      <div className="px-3 py-2 border-b border-gray-200" onMouseDown={e => e.preventDefault()}>
        <div className="text-xs font-semibold text-black-88 mb-1">Show data for</div>
        <Radio.Group
          value={mode}
          onChange={e => {
            e.stopPropagation();
            onModeChange?.(e.target.value);
          }}
        >
          <Radio value={ENTITY_MODE.USERS}>Users</Radio>
          <Radio value={ENTITY_MODE.TEAMS}>Teams</Radio>
        </Radio.Group>
      </div>
      {menu}
    </>
  );

  // When the dropdown closes, if the user toggled to a different mode but
  // never selected anything (so the active filter is still the other mode),
  // snap the visible toggle back. Without this, the closed Select shows
  // "Select teams" while queries are still filtered by users.
  const handleDropdownVisibleChange = open => {
    if (open) return;
    if (!activeMode) return;
    if (mode === activeMode) return;
    // But if the user DID pick something in the toggled-to mode, keep that mode
    // — reverting here is what made a fresh selection snap back to the old one.
    if (value && value?.length) return;
    onModeChange?.(activeMode);
  };

  // Single-select shows the one real pick (no "All" chip — that option doesn't
  // exist here); multi-select keeps the array (incl. the "All" chip).
  const selectValue = multiple ? displayValue : value && value.length ? +value[0] : undefined;

  return (
    <Select
      mode={multiple ? "multiple" : undefined}
      showSearch
      allowClear={!disabled}
      maxTagCount="responsive"
      placeholder={placeholder}
      className={className}
      value={selectValue}
      onChange={handleChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      options={options}
      loading={loading}
      disabled={disabled}
      notFoundContent={notFoundContent}
      dropdownRender={allowTeams ? dropdownRender : undefined}
      filterOption={(input, option) => {
        // Team labels are JSX (icon + name); fall back to the `name` sidecar
        // we attach in `options` so search still works after the labels
        // become non-strings.
        const text = option?.name ?? (typeof option?.label === "string" ? option.label : "");
        return String(text).toLowerCase().includes(input.toLowerCase());
      }}
    />
  );
};

export default EntityFilter;
